import * as React from 'react';

import AsyncRecorder from '../video/AsyncRecorder';
import RecordType from '../data/RecordType';

import {
  storeDispatch,
  getStoreState,
  filterCache,
  getRenderer,
  getCanvasScale,
  getStageBackground
} from '../state/Store';
import * as Actions from '../state/actions';
import { Resolution } from '../data/Resolution';
import {
  generateClassName,
  isEditingText,
  isMobileLayout,
  isPickingColor,
  openURLFromLite,
  pause,
  rotatedRectangleContainsPoint
} from '../utils';
import { AddGraphics } from '../onboarding/AddGraphics';
import Layer from '../data/Layer';
import { Soundtrack } from '../audio/Soundtrack';
import { MotionSource, MotionSourceType } from '../motion/MotionSource';
import { VIDEO_EFFECTS_FADE_IN_OUT_DURATION } from '../data/Config';
import PixelRatio, { PIXEL_RATIO_HIGH } from '../data/PixelRatio';
import Recorder from '../video/Recorder';
import OptionsBar from './OptionsBar';
import FlyerType from '../data/FlyerType';
import { setStage, shouldShowWatermark } from '../state/Store';
import { Frame } from '../data/Frame';
import { BoundingBox } from './BoundingBox';
import { PictureFrame } from '../data/PictureFrame';
import { FrameType } from '../data/FrameType';
import { TemplateEditMode } from '../state/AppState';
import { HoverBox } from './HoverBox';
import { PointData } from '../data/FlyerData';
import { Effect } from '@beatflyer/shaders-lib';
import { ShapeEditor } from './ShapeEditor';
import { getShapePIXIRectangle } from 'utils/GraphicsUtils';
import { EditBoundingBoxCommand } from 'state/commands/EditBoundingBoxCommand';
import { EditBoundingBoxRotationCommand } from 'state/commands/EditBoundingBoxRotationCommand';
import { PlatformType } from 'data/PlatformType';
import BlendMode from 'data/BlendMode';
import { ImportDesign } from 'onboarding/ImportDesign';

// const getElementHeight = (selector: string): number => {
//   const node = document.querySelector(selector);
//   if (node) {
//     return (node as HTMLElement).offsetHeight;
//   }
//   return 0;
// };

const PIXI = (window as any).PIXI;
PIXI.settings.PRECISION_FRAGMENT = PIXI.PRECISION.HIGH;

// const caret: PIXI.Graphics = new PIXI.Graphics();

interface Props {
  resolution: Resolution;
  previewPixelRatio: PixelRatio;
  layers: Layer[];
  motionSource: MotionSource;
  soundtrack: Soundtrack | null;
  isPlaying: boolean;
  isPreviewingEffect: boolean;
  subscriptionPlan: string | null;
  selectedFrame: Frame | null;
  selectedMask: Layer | null;
  recorder: Recorder | null;
}

interface State {
  hoverFrame: Frame | null;
  frameDownMousePosition: PointData | null;
}
export default class extends React.Component<Props, State> {
  state: State = {
    hoverFrame: null,
    frameDownMousePosition: null
  };

  private layers: Layer[];
  private animationFrameReq: number = -1;

  private renderer: PIXI.WebGLRenderer;
  private stage: PIXI.Container;
  private filtersCount: number;
  private watermark: PIXI.Sprite | null;
  // private startingTime: number = Date.now();
  // private caretInterval = -1;

  // private textController: TextEditingController | null = null;
  private el = React.createRef<HTMLDivElement>();
  private artboard = React.createRef<HTMLDivElement>();

  private mouseIsDown = false;

  componentDidMount() {
    this.recreatePixiStage(this.props.resolution);
    this.recreatePixiTree(this.props.layers, 'showcase item did mount');

    if (this.props.motionSource) {
      this.props.motionSource.on('loop', this.soundtrackDidLoop, this);
    }

    if (this.props.isPlaying) {
      this.play();
    } else {
      this.renderer.render(this.stage);
    }

    // document.addEventListener('visibilitychange', this.didChangeVisibility);
    // window.addEventListener('mousedown', this.didClickOnWindow);
    // window.addEventListener('resize', this.checkControlsVisibility);
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('keyup', this.onKeyUp);
    document.addEventListener(
      Actions.TEMPLATE_PROPERTY_CHANGED,
      this.didChangeTemplateUI
    );
    document.addEventListener(Actions.IMAGE_GIF_ANIMATED, this.didAnimateGif);
    // this.checkControlsVisibility();
  }
  componentWillUnmount() {
    this.pauseAnimation();
    const motionSource: MotionSource = this.props.motionSource;
    if (motionSource) {
      motionSource.removeListener('loop', this.soundtrackDidLoop, this);
    }
    // document.removeEventListener('visibilitychange', this.didChangeVisibility);
    // window.removeEventListener('mousedown', this.didClickOnWindow);
    // window.removeEventListener('resize', this.checkControlsVisibility);
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener(
      Actions.TEMPLATE_PROPERTY_CHANGED,
      this.didChangeTemplateUI
    );
    document.removeEventListener(
      Actions.IMAGE_GIF_ANIMATED,
      this.didAnimateGif
    );
    if (this.props.selectedFrame) {
      // is this needed?
      this.props.selectedFrame.off('update', this.onFrameUpdate);
    }
  }
  didChangeTemplateUI = () => {
    if (!this.props.isPlaying) {
      this.update();
    }
  };
  didAnimateGif = () => {
    if (!this.props.isPlaying) {
      this.update();
    }
  };
  /*
  didClickOnWindow = (e: MouseEvent) => {
    if (!this.state.isEditingText) {
      return;
    }
    if (!this.el.current) {
      return;
    }
    if (!this.el.current.contains(e.target as HTMLElement)) {
      this.deselectIfNeeded();
    }
  };*/

  deselectIfNeeded = () => {
    if (this.props.selectedFrame) {
      storeDispatch(Actions.unselectFrame());
    } else if (this.props.selectedMask) {
      storeDispatch(Actions.unselectMask());
    }
  };

  onKeyDown = (event: KeyboardEvent) => {
    if (this.props.selectedFrame && !isEditingText()) {
      if (
        event.key === 'ArrowUp' ||
        event.code === 'ArrowUp' ||
        event.key === 'ArrowDown' ||
        event.code === 'ArrowDown'
      ) {
        event.preventDefault();
      }
    }
    if (document.querySelector('input.custom-property-value')) {
      // is editing property value
      return;
    }
    if (document.querySelector('.overlay')) {
      // is showing an overlay
      return;
    }
    if (isEditingText()) {
      return;
    }
    if (event.ctrlKey || event.metaKey) {
      if (
        event.code === 'KeyZ' &&
        document.getElementById('undo-redo') !== null
      ) {
        if (event.shiftKey) {
          // REDO
          console.log('REDO');
          storeDispatch(Actions.redo());
        } else {
          // UNDO
          storeDispatch(Actions.undo());
          console.log('UNDO');
        }
        event.preventDefault();
      }
    }
  };

  onKeyUp = (event: KeyboardEvent) => {
    if (document.querySelector('input.custom-property-value')) {
      // is editing property value
      return;
    }
    if (document.querySelector('.overlay')) {
      // is showing an overlay
      return;
    }
    console.log(`key code:"${event.code}"`);
    if (event.code === 'Escape') {
      this.deselectIfNeeded();
      return;
    }
    if (
      !this.props.selectedFrame &&
      !isEditingText() &&
      event.code === 'Space'
    ) {
      storeDispatch(Actions.clickedSpacebar());
      return;
    }
    if (isEditingText()) {
      return;
    }
    if (!this.props.selectedFrame) {
      return;
    }
    if (event.code === 'Backspace' || event.key === 'Delete') {
      if (
        !isPickingColor() &&
        !document.querySelector('.text-settings-popover')
      ) {
        // is not editing hex code
        storeDispatch(Actions.removeFrameOrLayer(this.props.selectedFrame));
      }
    } else if (event.code === 'ArrowLeft') {
      // move left
      this.nudgeBoundingBox(new PIXI.Point(-1, 0), event.shiftKey);
    } else if (event.code === 'ArrowRight') {
      // move right
      this.nudgeBoundingBox(new PIXI.Point(1, 0), event.shiftKey);
    } else if (event.code === 'ArrowUp') {
      // move up
      this.nudgeBoundingBox(new PIXI.Point(0, -1), event.shiftKey);
    } else if (event.code === 'ArrowDown') {
      // move down
      this.nudgeBoundingBox(new PIXI.Point(0, 1), event.shiftKey);
    }
  };

  // checkControlsVisibility = () => {
  //   if (getStoreState().app.isBeatflyerLite || this.el.current === null) {
  //     return;
  //   }
  //   const headerHeight = getElementHeight('.header');
  //   const optionsBarHeight = getElementHeight('.options-bar');
  //   const canvasHeight = getElementHeight('.canvas');
  //   const windowHeight = window.innerHeight as number;
  //   const floatControls =
  //     canvasHeight + optionsBarHeight > windowHeight - headerHeight - 230;
  //   toggleClass(this.el.current, 'showcase-item--floatcontrols', floatControls);
  // };

  // didChangeVisibility = () => {
  //   if (document.hidden) {
  //     if (
  //       this.props.recorder &&
  //       this.props.recorder.type === RecordType.VideoLive
  //     ) {
  //       const type = this.props.recorder.type;
  //       // is recording
  //       this.cancelRecording().then(() => {
  //         storeDispatch(
  //           Actions.showAlert(
  //             new Alert(
  //               'Recording stopped',
  //               [
  //                 'This browser tab got hidden and the recording has been consequently compromised.',
  //                 'Please keep this tab always visible when you are recording.'
  //               ],
  //               'Restart recording',
  //               () => storeDispatch(Actions.startRecording(type))
  //             )
  //           )
  //         );
  //         setTimeout(() => storeDispatch(Actions.togglePlayback()), 200);
  //       });
  //     }
  //   }
  // };
  recreatePixiStage(resolution: Resolution, forceHighRes: boolean = false) {
    console.log('Recreate PIXI Stage');
    const el = this.el.current;
    if (el === null) {
      return;
    }
    const container = el.querySelector('.artboard')!;
    if (this.renderer) {
      container.removeChild(this.renderer.view);
      // try {
      //   this.renderer.gl!.getExtension('WEBGL_lose_context')!.loseContext();
      // } catch (e) {
      //   reportBFError(e);
      // }
      filterCache.destroy();
      // this.renderer.destroy();
    }
    const pixelRatio = forceHighRes
      ? PIXEL_RATIO_HIGH
      : this.props.previewPixelRatio;
    const stageWidth: number = resolution.width * pixelRatio.value;
    const stageHeight: number = resolution.height * pixelRatio.value;
    this.renderer = getRenderer();
    this.renderer.resize(stageWidth, stageHeight);
    // this.renderer.view.style.width =
    //   resolution.width * resolution.viewScale + 'px';
    // this.renderer.view.style.height =
    //   resolution.height * resolution.viewScale + 'px';
    container.appendChild(this.renderer.view);

    this.stage = new PIXI.Container();

    setStage(this.stage);
  }
  play() {
    console.log('ITEM PLAY');
    if (this.props.motionSource && !this.props.recorder) {
      this.props.motionSource.play();
    }
    this.layers.forEach(layer => {
      layer.play(0);
    });
    this.startAnimation();
  }
  resume = async () => {
    console.log('ITEM RESUME');
    if (!this.props.recorder) {
      this.props.motionSource.resume();
    }
    const lastTimeInSeconds = this.props.motionSource.getCurrentTime() / 1000;
    await Promise.all(
      this.layers.map(layer => {
        return layer.play(lastTimeInSeconds);
      })
    );
    this.startAnimation();
  };
  pause() {
    if (this.props.motionSource) {
      this.props.motionSource.pause();
    }
    this.layers.forEach(layer => {
      layer.pause();
    });
    this.pauseAnimation();
  }
  startAnimation() {
    if (this.animationFrameReq === -1) {
      this.animationFrameReq = 0;
      this.update();
    }
  }
  pauseAnimation() {
    if (this.animationFrameReq > -1) {
      cancelAnimationFrame(this.animationFrameReq);
      this.animationFrameReq = -1;
    }
  }

  soundtrackDidLoop() {
    // console.log('SOUNDTRACK DID LOOP');
    this.layers.forEach(layer => {
      layer.play(0);
    });
  }

  startRecording() {
    console.log('ShowcaseItem.startRecording');
    this.resetFilters();
    filterCache.destroy();
    // this.startingTime = Date.now();
  }

  async componentDidUpdate(prevProps: Props, prevState: any) {
    // if (
    //   prevProps.flowState !== FlowState.EditingFrame &&
    //   this.props.flowState === FlowState.EditingFrame
    // ) {
    //   // listen to mouse interactions
    //   return;
    // }
    let hasChangedSoundtrack = false;
    let hasChangedState = false;

    if (this.props.motionSource !== prevProps.motionSource) {
      this.pauseAnimation();
      if (prevProps.motionSource) {
        prevProps.motionSource.removeListener(
          'loop',
          this.soundtrackDidLoop,
          this
        );
      }
      if (this.props.motionSource) {
        this.props.motionSource.on('loop', this.soundtrackDidLoop, this);
      }
      hasChangedSoundtrack = true;
    }

    // if (this.props.flowState !== prevProps.flowState) {
    //   switch (this.props.flowState) {
    //     case FlowState.Recording:
    //       this.startRecording();
    //       break;
    //     default:
    //       break;
    //   }
    //   hasChangedState = true;
    // }

    let updatePixiReason: string | null = null;
    if (
      this.props.layers.filter(layer => {
        return layer.needsUpdate;
      }).length
    ) {
      updatePixiReason = 'one or more layers need update';
    } else {
      let newFiltersCount = 0;
      let visibleLayersCount = 0;
      this.props.layers.forEach((layer, index) => {
        // this.layers[index].audioSource = layer.audioSource; // TODO: What the hell???
        if (layer.visible) {
          visibleLayersCount++;
        }
        newFiltersCount += layer.visibleEffectsCount;
        if (layer.compositionEffect?.visible) {
          newFiltersCount++;
        }
      });
      if (visibleLayersCount !== this.layers.length) {
        updatePixiReason = 'different visible layers amount';
      } else if (newFiltersCount !== this.filtersCount) {
        updatePixiReason = 'different filters amount';
      }
    }
    if (prevProps.selectedFrame !== this.props.selectedFrame) {
      updatePixiReason = 'different selected frame';
    }

    if (prevProps.selectedFrame) {
      prevProps.selectedFrame.off('update', this.onFrameUpdate);
    }
    if (this.props.selectedFrame) {
      this.props.selectedFrame.on('update', this.onFrameUpdate);
    }

    if (
      prevProps.resolution !== this.props.resolution ||
      prevProps.previewPixelRatio !== this.props.previewPixelRatio
    ) {
      this.recreatePixiStage(this.props.resolution);
      updatePixiReason = 'different resolution';
    }

    if (this.props.subscriptionPlan !== prevProps.subscriptionPlan) {
      updatePixiReason = 'different subscription plan';
    }
    if (updatePixiReason) {
      this.recreatePixiTree(this.props.layers, updatePixiReason);
      if (!this.props.isPlaying) {
        await this.update();
      }
    }

    if (this.props.isPlaying !== prevProps.isPlaying) {
      if (this.props.isPlaying) {
        if (hasChangedSoundtrack || hasChangedState) {
          this.play();
        } else {
          this.resume();
        }
      } else {
        this.pause();
      }
    } else {
      if (hasChangedSoundtrack && this.props.isPlaying) {
        this.play();
      }
    }
    // this.checkControlsVisibility();
    if (prevProps.recorder === null && this.props.recorder !== null) {
      this.startRecording();
    }
  }

  getWatermark(): PIXI.Sprite {
    if (!this.watermark) {
      this.watermark = PIXI.Sprite.from('/media/watermark.png');
    }
    return this.watermark!;
  }

  recreatePixiTree(layers: Layer[], reason: string) {
    console.log(`Recreate PIXI Tree because of ${reason}`);
    for (var i = this.stage.children.length - 1; i >= 0; i--) {
      this.stage.removeChild(this.stage.children[i]);
    }

    const { width, height } = this.props.resolution;

    const bg = getStageBackground();
    bg.width = width;
    bg.height = height;
    this.stage.addChild(bg);

    this.layers = [];
    layers.map(layer => {
      if (layer.visible) {
        return this.addLayer(layer);
      } else {
        return null;
      }
    });

    const canRecord =
      getStoreState().flyer.type !== FlyerType.CopyrightedExample;
    if (canRecord && shouldShowWatermark()) {
      let watermark = this.getWatermark();
      watermark.x = width - 314;
      watermark.y = height - 105;
      this.stage.addChild(watermark);
    }

    this.filtersCount = 0;
    this.layers.map(layer => {
      if (layer.sprite!.filters) {
        this.filtersCount += layer.sprite!.filters!.length;
        if (layer.blendMode !== BlendMode.Normal) {
          this.filtersCount--;
        }
      }
      if (layer.compositionEffect?.visible) {
        this.filtersCount++;
      }
      return layer;
    });
  }

  addLayer(layer: Layer) {
    this.stage.addChild(layer.sprite);
    if (layer.sprite.mask) {
      this.stage.addChild(layer.sprite.mask);
    }
    this.layers.push(layer);
    layer.needsUpdate = false;

    if (!layer.isLoaded) {
      layer.currentFrame.removeListener('load');
      layer.currentFrame.once('load', () => {
        this.recreatePixiTree(this.layers, 'loaded frame');
        // console.log('LAYER LOADED');
        if (this.props.isPlaying) {
          const currentTimeInSeconds =
            this.props.motionSource.getVideoCurrentTime() * 0.001;
          layer.play(currentTimeInSeconds);
        } else {
          this.update();
        }
      });
    }
  }

  resetFilters() {
    const len = this.layers.length;
    for (let i = 0; i < len; i++) {
      const layer = this.layers[i];
      if (layer.effects) {
        layer.effects.map((effect: Effect) => {
          if (!effect.isSplitEffect) {
            effect.reset();
          }
          return effect;
        });
      }
    }
  }

  getAudioSpectrum(): Uint8Array {
    const { recorder, motionSource, soundtrack } = this.props;
    if (!motionSource.hasAudio && soundtrack === null) {
      return new Uint8Array([]);
    }
    if (recorder) {
      return recorder.getSpectrum();
    } else {
      if (!soundtrack) {
        console.warn('There is no soundtrack but motion source has audio');
        return new Uint8Array([]);
      }
      return soundtrack.getSpectrum()!;
    }
  }

  getAudioWaveform(): Uint8Array {
    const { recorder, motionSource, soundtrack } = this.props;
    if (!motionSource.hasAudio && soundtrack === null) {
      return new Uint8Array([]);
    }
    if (recorder) {
      return recorder.getWaveform();
    } else {
      if (!soundtrack) {
        console.warn('There is no soundtrack but motion source has audio');
        return new Uint8Array([]);
      }
      return soundtrack.getWaveform()!;
    }
  }

  update = async () => {
    // if (this.animationFrameReq === -1) {
    //   return;
    // }
    cancelAnimationFrame(this.animationFrameReq);
    const { motionSource, soundtrack, recorder } = this.props;

    // console.log('update');

    if (motionSource) {
      soundtrack?.update();

      const duration = motionSource.getDuration();
      const currentTime =
        recorder && recorder instanceof AsyncRecorder
          ? (recorder as AsyncRecorder).getCurrentTime()
          : motionSource.getCurrentTime();
      let opacity = 1;

      if (
        ![
          MotionSourceType.FullOn,
          MotionSourceType.LinearLoop,
          MotionSourceType.Binary
        ].includes(motionSource.type)
      ) {
        // console.log(
        //   currentTime + ' / ' + duration + ' : ' + currentTime / duration
        // );
        if (currentTime === duration) {
          opacity = 0;
        } else if (currentTime < VIDEO_EFFECTS_FADE_IN_OUT_DURATION) {
          opacity = currentTime / VIDEO_EFFECTS_FADE_IN_OUT_DURATION;
        } else if (
          currentTime >
          duration - VIDEO_EFFECTS_FADE_IN_OUT_DURATION
        ) {
          const d = duration - currentTime;
          opacity = d / VIDEO_EFFECTS_FADE_IN_OUT_DURATION;
        }
      }

      opacity = Math.max(0, Math.min(1, opacity));

      /*if (motionSource.isPlaying && soundtrack) {
        const fadeLength = soundtrack.audioFadeLength;
        if (fadeLength > 0) {
          let volume = 1;
          if (currentTime < fadeLength) {
            if (currentTime === 0) {
              volume = 0;
            } else {
              volume = currentTime / fadeLength;
            }
          } else if (currentTime > duration - fadeLength) {
            if (currentTime === duration) {
              volume = 0;
            } else {
              volume = (duration - currentTime) / fadeLength;
            }
          }
          soundtrack.volume = volume;
          if (recorder && recorder instanceof VideoRecorder) {
            (recorder as VideoRecorder).setVolume(volume);
          }
        }
      }*/
      let position = currentTime / duration;
      if (recorder && recorder instanceof AsyncRecorder) {
        position = (recorder as AsyncRecorder).getPosition();
      }

      if (recorder && recorder.type === RecordType.GIF) {
        // if it's recording a GIF, ignore opacity and layer position
        opacity = 1;
        position = 0;
      }

      let spectrum = this.getAudioSpectrum();
      let waveform = this.getAudioWaveform();
      let time = currentTime / 1000; // (Date.now() - this.startingTime) / 1000;
      if (recorder && recorder instanceof AsyncRecorder) {
        // keep the video layer in sync
        for (const layer of this.layers) {
          if (layer.isVideo) {
            await (layer.frames[0] as PictureFrame).setVideoCurrentTime(time);
          }
        }
      }
      // console.log("MP: " + motionPosition.toFixed(2) + " LP: " + position.toFixed(2));
      const isRecordingGif =
        recorder !== null && recorder.type === RecordType.GIF;
      const defaultLevel = motionSource.getLevelAt(currentTime);
      // default level is used to decide which frame to show when the layer has multiple frames and synched playback
      // now is not possible anymore to choose layer specific settings
      this.layers.forEach(layer => {
        layer.update(
          position,
          opacity,
          defaultLevel,
          spectrum,
          waveform,
          time,
          isRecordingGif
        );
        if (!isRecordingGif && layer.selectedFrame === null) {
          const compositionEffect =
            layer.previewedCompositionEffect || layer.compositionEffect;
          compositionEffect?.update(time, duration * 0.001);
        }
      });
      // if (this.recorder) {
      //   console.log('OPACITY: ' + opacity);
      // }
      filterCache.update();
    }

    // console.log('RENDER');

    this.renderer.render(this.stage);
    if (recorder) {
      if (recorder instanceof AsyncRecorder && recorder.waitingForFrames) {
        (recorder as AsyncRecorder).update();
      }
    }
    if (this.animationFrameReq > -1) {
      this.animationFrameReq = requestAnimationFrame(this.update);
    }
  };

  // resetCaretAnimation() {
  //   window.clearInterval(this.caretInterval);
  //   caret.alpha = 1;
  //   this.renderer.render(this.stage);
  //   this.caretInterval = window.setInterval(() => {
  //     caret.alpha = caret.alpha === 0 ? 1 : 0;
  //     this.renderer.render(this.stage);
  //   }, 500);
  // }

  // updateCaretPosition = (p: Point) => {
  //   const { selectedFrame } = getStoreState().app;
  //   if (selectedFrame && selectedFrame instanceof TextFrame) {
  //     const caretPosition = (selectedFrame as TextFrame).getCaretPositionAt(
  //       p.x,
  //       p.y
  //     );
  //     console.log(`caret position: ` + caretPosition);
  //     if (this.textController) {
  //       this.textController.setCaretPosition(caretPosition);
  //     }
  //   }
  // };

  onFrameUpdate = () => {
    const { selectedFrame } = getStoreState().app;
    if (!selectedFrame) {
      return;
    }
    /*if (selectedFrame.type === FrameType.Text && this.state.isEditingText) {
      this.updateCaret();
    }*/
    this.renderer.render(this.stage);
  };

  // onTextUpdate = () => {
  //   const { selectedFrame } = getStoreState().app;
  //   if (!selectedFrame) {
  //     return;
  //   }
  //   this.updateText(this.textController!.text);
  //   storeDispatch(Actions.editedText());
  // };

  // updateText = (value: string) => {
  //   const { selectedFrame } = getStoreState().app;
  //   (selectedFrame as TextFrame).text = value;
  //   // this.updateCaret();
  //   // this.resetCaretAnimation();
  //   this.forceUpdate();
  //   this.renderer.render(this.stage);
  // };

  nudgeBoundingBox = (offset: PIXI.Point, leap: boolean) => {
    const { selectedFrame } = getStoreState().app;
    if (selectedFrame) {
      const { x, y, width, height } = selectedFrame.boundingBox;
      const newRect = new PIXI.Rectangle(
        x + offset.x * (leap ? 10 : 1),
        y + offset.y * (leap ? 10 : 1),
        width,
        height
      );
      this.onBoundingBoxUpdate(newRect);
    }
  };

  onBoundingBoxUpdate = (newRect: PIXI.Rectangle) => {
    storeDispatch(new EditBoundingBoxCommand(newRect));
    // this.forceUpdate();
  };

  onBoundingBoxRotate = (newAngle: number) => {
    storeDispatch(new EditBoundingBoxRotationCommand(newAngle));
    // this.forceUpdate();
  };

  /*updateCaret() {
    const { selectedFrame } = getStoreState().app;
    if (selectedFrame) {
      const textFrame = selectedFrame as TextFrame;
      const lineWidth = 2 / getCanvasScale(); // this.props.resolution.viewScale;
      const caretRect = textFrame.getCaretRect(
        this.textController!.caretPosition
      );
      const textStyle = textFrame.textStyle;
      let color = 0;
      if (Array.isArray(textStyle.fill)) {
        color = PIXI.utils.string2hex(
          textStyle.fill[textStyle.fill.length - 1]
        );
      } else {
        let hex = textStyle.fill as string;
        if (
          hex.length === 9 &&
          hex.endsWith('00') &&
          textStyle.stroke &&
          textStyle.strokeThickness
        ) {
          hex = textStyle.stroke as string;
        }
        color = PIXI.utils.string2hex(hex);
      }
      caret.clear();
      caret.lineStyle(lineWidth * 2, color, 1);
      caret.moveTo(0, 0);
      caret.lineTo(0, caretRect.height);
      caret.x = caretRect.x;
      caret.y = caretRect.y;
    }
  }*/

  // didClickWatermark = (e: React.MouseEvent<HTMLDivElement>) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   storeDispatch(Actions.showBuyingOptions());
  //   return false;
  // };

  renderGoPro = (): JSX.Element | null => {
    const appState = getStoreState().app;
    if (appState.isBeatflyerLite) {
      return (
        <div className="figma-export-ui">
          <div className="pro-ad">
            {/* For better rendering, sync effects to the music, audio visualizers,
            and more,{' '}
            <strong>
              <a
                href="#"
                onClick={e => {
                  e.preventDefault();
                  openURLFromLite('https://editor.beatflyer.com');
                  trackEvent('clickedGoPro', { location: 'bottom' });
                }}
              >
                try Beatflyer Pro
              </a>
            </strong> */}
          </div>

          <button
            className="large"
            style={{ color: 'var(--primary-text-color)', opacity: 0.6 }}
            onMouseEnter={e => {
              (e.target as HTMLElement).style.opacity = '1';
            }}
            onMouseLeave={e => {
              (e.target as HTMLElement).style.opacity = '0.6';
            }}
            onClick={e => {
              e.preventDefault();
              openURLFromLite('https://learn.beatflyer.com');
            }}
          >
            Documentation
          </button>

          <button
            className="large primary"
            onClick={() => {
              storeDispatch(Actions.showExportOptions());
            }}
            disabled={appState.controllerHasConfirmation}
          >
            Export
          </button>
        </div>
      );
    }
    return null;
  };

  /*didStartEditingContent = () => {
    const selectedLayer = this.layers.find(l =>
      l.frames.includes(this.props.selectedFrame!)
    )!;
    selectedLayer.isEditingFrame = true;
    this.setState({ isEditingText: true });
    this.textController = new TextEditingController(
      (this.props.selectedFrame as TextFrame).text
    );
    this.textController.on('update', this.onTextUpdate);
    this.updateCaret();
    this.stage.addChild(caret);
    this.resetCaretAnimation();
  };*/

  /*didStopEditingContent = () => {
    const selectedLayer = this.layers.find(l =>
      l.frames.includes(this.props.selectedFrame!)
    )!;
    selectedLayer.isEditingFrame = false;
    this.textController?.off('update', this.onTextUpdate);
    this.textController?.dispose();
    this.textController = null;
    this.setState({ isEditingText: false });
    window.clearInterval(this.caretInterval);
    this.stage.removeChild(caret);
    this.renderer.render(this.stage);
  };*/

  getFrameAt = (x: number, y: number): Frame | null => {
    if (!this.artboard.current) {
      return null;
    }
    const targetBox = this.artboard.current.getBoundingClientRect();
    x -= targetBox.x;
    y -= targetBox.y;
    const scale = getCanvasScale();
    const flyerX = x * (1 / scale);
    const flyerY = y * (1 / scale);

    const { width, height } = this.props.resolution;
    if (flyerX >= 0 && flyerY >= 0 && flyerX < width && flyerY < height) {
      for (let i = this.layers.length - 1; i >= 0; i--) {
        const layer = this.layers[i];
        if (!layer.locked) {
          const frame = layer.selectedFrame || layer.currentFrame;
          if (frame.isResizable) {
            if (frame.rotation === 0) {
              if (frame.boundingBox.contains(flyerX, flyerY)) {
                return frame;
              }
            } else {
              if (
                rotatedRectangleContainsPoint(
                  frame.boundingBox,
                  frame.rotation,
                  flyerX,
                  flyerY
                )
              ) {
                return frame;
              }
            }
          }
        }
      }
    }
    return null;
  };

  didTouch = () => {
    storeDispatch(Actions.togglePlayback());
  };

  didClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (document.querySelector('.react-tiny-popover-container')) {
      return;
    }
    if (document.querySelector('.effect-picker')) {
      return;
    }
    if (document.querySelector('.menu-panel')) {
      return;
    }
    if (document.querySelector('.shape-editor')) {
      return;
    }
    if (getStoreState().app.templateEditMode === TemplateEditMode.Simple) {
      if ((e.target as HTMLElement).nodeName.toLowerCase() === 'canvas') {
        // TODO: show toast notification
        alert(
          'Switch the left sidebar tab to "ADVANCED" to edit individual elements directly.'
        );
      }
      return;
    }
    this.mouseIsDown = true;
    if (!this.layers.length) {
      return;
    }
    const target = e.target as HTMLElement;
    /*if (this.state.isEditingText) {
      if (target.className !== 'box') {
        this.deselectIfNeeded();
      }
      return;
    }*/
    if (target.className === 'box' || target.className === 'draggable-corner') {
      return;
    }
    if (!this.artboard.current) {
      return;
    }

    const frame = this.getFrameAt(e.clientX, e.clientY);
    if (frame) {
      this.setState(
        { frameDownMousePosition: { x: e.clientX, y: e.clientY } },
        async () => {
          if (this.props.selectedFrame) {
            storeDispatch(Actions.unselectFrame());
            await pause(1);
          } else if (this.props.selectedMask) {
            storeDispatch(Actions.unselectMask());
            await pause(1);
          }
          storeDispatch(Actions.selectFrame(frame));
        }
      );
    } else {
      this.deselectIfNeeded();
    }
  };

  didMoveMouse = (e: React.MouseEvent<HTMLDivElement>) => {
    if (this.mouseIsDown) {
      return;
    }
    if (!this.layers.length) {
      return;
    }
    if (this.props.selectedMask) {
      return;
    }
    if (document.querySelector('.effect-picker')) {
      return;
    }
    if (document.querySelector('.menu-panel')) {
      return;
    }
    if (document.querySelector('.shape-editor')) {
      return;
    }
    const target = e.target as HTMLElement;
    if (target.className === 'draggable-corner') {
      this.setState({ hoverFrame: null });
      return;
    }
    let hoverFrame = this.getFrameAt(e.clientX, e.clientY);
    if (hoverFrame === this.props.selectedFrame) {
      hoverFrame = null;
    }
    this.setState({ hoverFrame });
  };

  didMouseUp = () => {
    this.mouseIsDown = false;
    if (this.state.frameDownMousePosition) {
      this.setState({ frameDownMousePosition: null });
    }
  };

  didClickHoverBox = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const frame = this.state.hoverFrame;
    const frameDownMousePosition = { x: event.clientX, y: event.clientY };
    this.setState({ hoverFrame: null, frameDownMousePosition }, async () => {
      if (this.props.selectedFrame) {
        storeDispatch(Actions.unselectFrame());
        await pause(1);
      } else if (this.props.selectedMask) {
        storeDispatch(Actions.unselectMask());
        await pause(1);
      }
      storeDispatch(Actions.selectFrame(frame));
    });
  };

  startEditingFrame = () => {
    getStoreState().app.selectedFrame?.startEditing();
  };

  render() {
    // console.log('RENDER ITEM...');
    // console.log('layers: ' + this.layers + ' length: ' + (this.layers ? this.layers.length : 0));
    const appState = getStoreState().app;
    const { editedShape, proposedEditedShape, platform } = appState;

    const hasResizableFrameSelected =
      (appState.selectedFrame && appState.selectedFrame.isResizable) ||
      appState.selectedMask !== null;

    const isToggleable =
      this.props.motionSource &&
      !hasResizableFrameSelected &&
      this.props.layers.length > 0 &&
      false;
    const modifiers = {
      play: true,
      record: false, // appState.flowState === FlowState.Recording,
      // intro: appState.flowState === FlowState.Intro,
      dimmed: false // appState.flowState === FlowState.ProcessingAudio
    };
    let className = 'showcase-item';
    for (var key in modifiers) {
      if (modifiers[key] === true) {
        className += ' showcase-item--' + key;
      }
    }
    let instructions = null;
    if (this.props.layers.length === 0 && !appState.isBeatflyerLite) {
      if (appState.importDesign) {
        instructions = <ImportDesign />;
      } else {
        instructions = <AddGraphics />;
      }
    }
    const flyerState = getStoreState().flyer;
    const canvasClassName: string = generateClassName('canvas', {
      toggleable: isToggleable,
      playing: this.props.isPlaying,
      paused: !this.props.isPlaying,
      cannotPlayAudio: !appState.userHasInteractedWithPage,
      empty: flyerState.layers.length === 0
    });

    // const canvasStyle: any = {
    //   width:
    //     this.props.resolution.width * this.props.resolution.viewScale + 'px',
    //   height:
    //     this.props.resolution.height * this.props.resolution.viewScale + 'px'
    // };

    const boundingBoxCanTriggerEdit =
      appState.selectedFrame !== null &&
      [FrameType.Text, FrameType.Shape, FrameType.SoundWave].includes(
        appState.selectedFrame.type
      );
    const canSelectFrames =
      (!appState.isBeatflyerLite || platform === PlatformType.Figma) &&
      appState.templateEditMode !== TemplateEditMode.Simple;
    const { hoverFrame } = this.state;
    let boundingBox: PIXI.Rectangle = new PIXI.Rectangle(0, 0, 0, 0);
    if (appState.selectedFrame) {
      boundingBox = appState.selectedFrame.boundingBox;
    } else if (appState.selectedMask && appState.selectedMask.mask) {
      boundingBox = getShapePIXIRectangle(appState.selectedMask.mask);
    }
    const isMobile =
      isMobileLayout() || appState.platform === PlatformType.iOSApp;
    return (
      <div
        className={className}
        onMouseDown={
          !appState.isBeatflyerLite || platform === PlatformType.Figma
            ? this.didClick
            : undefined
        }
        onMouseMove={canSelectFrames ? this.didMoveMouse : undefined}
        onMouseUp={canSelectFrames ? this.didMouseUp : undefined}
        onTouchEnd={this.didTouch}
        ref={this.el}
      >
        <div className="centered-content">
          {appState.isBeatflyerLite === false && !isMobile && (
            <OptionsBar
              resolution={this.props.resolution}
              previewPixelRatio={this.props.previewPixelRatio}
              collapsed={appState.templateEditMode === TemplateEditMode.Simple}
            />
          )}
          <div className={canvasClassName}>
            <div className="artboard" ref={this.artboard} />
            {instructions && <div className="instructions">{instructions}</div>}
            {/* {canRecord && !isUserPro() && !appState.isFigmaPlugin && (
              <div
                className="remove-watermark"
                onClick={this.didClickWatermark}
              />
            )} */}
            {editedShape !== null && proposedEditedShape !== null && (
              <ShapeEditor
                points={proposedEditedShape}
                resolution={flyerState.resolution}
              />
            )}
            {editedShape === null && hasResizableFrameSelected && (
              <BoundingBox
                maintainAspectRatio={
                  appState.selectedFrame instanceof PictureFrame
                }
                reactangle={boundingBox}
                rotation={
                  appState.selectedFrame?.rotation ??
                  appState.selectedMask?.maskRotation ??
                  0
                }
                canRotate={appState.selectedFrame?.canRotate ?? true}
                stageResolution={flyerState.resolution}
                onChange={this.onBoundingBoxUpdate}
                onRotate={this.onBoundingBoxRotate}
                onClick={() => {
                  /* do nothing */
                }}
                // isEditingContent={false}
                canEditContent={boundingBoxCanTriggerEdit}
                onStartedEditingContent={this.startEditingFrame}
                initialMousePosition={this.state.frameDownMousePosition}
                // onStoppedEditingContent={() => {
                //   /* do nothing */
                // }}
                selectedFrame={appState.selectedFrame!}
              />
            )}
            {hoverFrame && (
              <HoverBox
                reactangle={hoverFrame.boundingBox}
                rotation={hoverFrame.rotation}
                stageResolution={flyerState.resolution}
                onClick={this.didClickHoverBox}
              />
            )}
          </div>
          {this.props.layers.length > 0 && this.renderGoPro()}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/showcase/ShowcaseItem.tsx